import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDate, formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Package,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  DollarSign,
  TrendingDown,
  Calculator,
  Boxes,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Asset {
  id: string;
  businessProfileId: string | null;
  businessProfileName: string | null;
  name: string;
  description: string | null;
  assetType: string;
  purchaseDate: string;
  purchasePrice: number;
  salvageValue: number;
  usefulLifeYears: number;
  depreciationMethod: string;
  macrsPropertyClass: string | null;
  datePlacedInService: string | null;
  disposalDate: string | null;
  disposalAmount: number | null;
  status: string;
  isSection179: boolean;
  section179Amount: number | null;
  vehicleBusinessUsePct: number | null;
  notes: string | null;
}

interface DepreciationRow {
  yearNumber: number;
  yearDate: string;
  depreciationAmount: number;
  accumulatedDepreciation: number;
  bookValue: number;
  section179Deduction: number;
}

interface BusinessProfile {
  id: string;
  name: string;
}

interface AssetSummary {
  totalAssets: number;
  activeAssets: number;
  totalPurchaseValue: number;
  activePurchaseValue: number;
  totalSection179: number;
}

const ASSET_TYPES = [
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'building', label: 'Building' },
  { value: 'furniture', label: 'Furniture' },
  { value: 'computer', label: 'Computer / Electronics' },
  { value: 'software', label: 'Software' },
  { value: 'other', label: 'Other' },
];

const DEPRECIATION_METHODS = [
  { value: 'straight_line', label: 'Straight-Line' },
  { value: 'declining_balance', label: 'Double Declining Balance' },
  { value: 'sum_of_years', label: "Sum of Years' Digits" },
  { value: 'macrs', label: 'MACRS' },
];

const IRS_USEFUL_LIFE: Record<string, number> = {
  vehicle: 5,
  equipment: 7,
  building: 39,
  furniture: 7,
  computer: 5,
  software: 3,
  other: 7,
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  disposed: 'Disposed',
  fully_depreciated: 'Fully Depreciated',
};

export function AssetsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [profileFilter, setProfileFilter] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [depreciationAsset, setDepreciationAsset] = useState<Asset | null>(null);
  const [formAssetType, setFormAssetType] = useState<string>('');

  const { data: profilesData } = useQuery({
    queryKey: ['businessProfiles'],
    queryFn: () => api.getBusinessProfiles(),
  });

  const { data: summaryData } = useQuery({
    queryKey: ['assetSummary', { businessProfileId: profileFilter }],
    queryFn: () => api.getAssetSummary({
      businessProfileId: profileFilter || undefined,
    }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['assets', { page, search, assetType: typeFilter, status: statusFilter, businessProfileId: profileFilter }],
    queryFn: () => api.getAssets({
      page,
      limit: 20,
      search: search || undefined,
      assetType: typeFilter || undefined,
      status: statusFilter || undefined,
      businessProfileId: profileFilter || undefined,
    }),
  });

  const { data: depreciationData, isLoading: depLoading } = useQuery({
    queryKey: ['assetDepreciation', depreciationAsset?.id],
    queryFn: () => api.getAssetDepreciation(depreciationAsset!.id),
    enabled: !!depreciationAsset,
  });

  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const summary = summaryData?.data as AssetSummary | undefined;
  const assets = (data?.data || []) as Asset[];
  const pagination = data?.pagination;
  const depreciation = (depreciationData?.data || []) as DepreciationRow[];

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createAsset(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['assetSummary'] });
      setDialogOpen(false);
      toast({ title: 'Asset created' });
    },
    onError: () => toast({ title: 'Failed to create asset', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.updateAsset(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['assetSummary'] });
      queryClient.invalidateQueries({ queryKey: ['assetDepreciation'] });
      setDialogOpen(false);
      setEditingAsset(null);
      toast({ title: 'Asset updated' });
    },
    onError: () => toast({ title: 'Failed to update asset', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAsset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['assetSummary'] });
      toast({ title: 'Asset deleted' });
    },
    onError: () => toast({ title: 'Failed to delete asset', variant: 'destructive' }),
  });

  function openCreate() {
    setEditingAsset(null);
    setFormAssetType('');
    setDialogOpen(true);
  }

  function openEdit(asset: Asset) {
    setEditingAsset(asset);
    setFormAssetType(asset.assetType);
    setDialogOpen(true);
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const assetType = fd.get('assetType') as string;
    const payload: Record<string, unknown> = {
      businessProfileId: fd.get('businessProfileId') || null,
      name: fd.get('name'),
      description: fd.get('description') || null,
      assetType,
      purchaseDate: fd.get('purchaseDate'),
      purchasePrice: parseFloat(fd.get('purchasePrice') as string),
      salvageValue: parseFloat(fd.get('salvageValue') as string) || 0,
      usefulLifeYears: parseInt(fd.get('usefulLifeYears') as string, 10),
      depreciationMethod: fd.get('depreciationMethod'),
      macrsPropertyClass: fd.get('macrsPropertyClass') || null,
      datePlacedInService: fd.get('datePlacedInService') || null,
      isSection179: fd.get('isSection179') === 'on',
      section179Amount: fd.get('section179Amount') ? parseFloat(fd.get('section179Amount') as string) : null,
      // Only relevant for vehicles. Form 4562 needs this for listed-property
      // depreciation. We send NULL when not a vehicle so non-vehicle rows
      // don't get a stray business-use % stored.
      vehicleBusinessUsePct: assetType === 'vehicle' && fd.get('vehicleBusinessUsePct')
        ? parseFloat(fd.get('vehicleBusinessUsePct') as string)
        : null,
      notes: fd.get('notes') || null,
    };

    if (editingAsset) {
      updateMutation.mutate({ id: editingAsset.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Assets & Depreciation</h1>
          <p className="text-muted-foreground">Track fixed assets and calculate depreciation schedules</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Asset
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10"><Boxes className="h-5 w-5 text-primary" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Active Assets</p>
                <p className="text-xl font-bold">{summary?.activeAssets || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10"><DollarSign className="h-5 w-5 text-blue-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Total Value</p>
                <p className="text-xl font-bold">{formatCurrency(summary?.activePurchaseValue || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10"><TrendingDown className="h-5 w-5 text-orange-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Total Assets</p>
                <p className="text-xl font-bold">{summary?.totalAssets || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10"><Calculator className="h-5 w-5 text-green-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Section 179</p>
                <p className="text-xl font-bold">{formatCurrency(summary?.totalSection179 || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search assets..."
            className="pl-9"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <Select value={typeFilter || 'all'} onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {ASSET_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="disposed">Disposed</SelectItem>
            <SelectItem value="fully_depreciated">Fully Depreciated</SelectItem>
          </SelectContent>
        </Select>
        <Select value={profileFilter || 'all'} onValueChange={(v) => { setProfileFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All profiles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All profiles</SelectItem>
            {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : assets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No assets found</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 text-sm font-medium">Name</th>
                  <th className="text-left p-3 text-sm font-medium">Type</th>
                  <th className="text-left p-3 text-sm font-medium">Business Profile</th>
                  <th className="text-left p-3 text-sm font-medium">Purchased</th>
                  <th className="text-right p-3 text-sm font-medium">Cost</th>
                  <th className="text-left p-3 text-sm font-medium">Method</th>
                  <th className="text-center p-3 text-sm font-medium">Life (yrs)</th>
                  <th className="text-center p-3 text-sm font-medium">Status</th>
                  <th className="text-right p-3 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((asset) => (
                  <tr key={asset.id} className="border-b hover:bg-muted/30">
                    <td className="p-3 text-sm font-medium">{asset.name}</td>
                    <td className="p-3 text-sm">{ASSET_TYPES.find(t => t.value === asset.assetType)?.label || asset.assetType}</td>
                    <td className="p-3 text-sm">{asset.businessProfileName || '—'}</td>
                    <td className="p-3 text-sm">{formatDate(asset.purchaseDate)}</td>
                    <td className="p-3 text-sm text-right font-medium">{formatCurrency(asset.purchasePrice)}</td>
                    <td className="p-3 text-sm">{DEPRECIATION_METHODS.find(m => m.value === asset.depreciationMethod)?.label || asset.depreciationMethod}</td>
                    <td className="p-3 text-sm text-center">{asset.usefulLifeYears}</td>
                    <td className="p-3 text-center">
                      <Badge variant={asset.status === 'active' ? 'default' : 'secondary'}>
                        {STATUS_LABELS[asset.status] || asset.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setDepreciationAsset(asset)}>
                        <Calculator className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(asset)}>Edit</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => { if (confirm('Delete this asset?')) deleteMutation.mutate(asset.id); }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {pagination.total} asset{pagination.total !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page} of {pagination.totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Depreciation Schedule Dialog */}
      <Dialog open={!!depreciationAsset} onOpenChange={(open) => { if (!open) setDepreciationAsset(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Depreciation Schedule: {depreciationAsset?.name}</DialogTitle>
            <DialogDescription>
              {depreciationAsset && (
                <>
                  {DEPRECIATION_METHODS.find(m => m.value === depreciationAsset.depreciationMethod)?.label} over {depreciationAsset.usefulLifeYears} years
                  {depreciationAsset.isSection179 && depreciationAsset.section179Amount
                    ? ` (Section 179: ${formatCurrency(depreciationAsset.section179Amount)})`
                    : ''}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {depLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : depreciation.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No depreciation data</p>
          ) : (
            <div className="space-y-4">
              {depreciationAsset?.isSection179 && depreciationAsset.section179Amount ? (
                <div className="rounded-lg bg-green-50 dark:bg-green-950/20 p-3 text-sm">
                  Section 179 immediate deduction: <strong>{formatCurrency(depreciationAsset.section179Amount)}</strong>
                </div>
              ) : null}
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-2 font-medium">Year</th>
                    <th className="text-left p-2 font-medium">Date</th>
                    <th className="text-right p-2 font-medium">Depreciation</th>
                    <th className="text-right p-2 font-medium">Accumulated</th>
                    <th className="text-right p-2 font-medium">Book Value</th>
                  </tr>
                </thead>
                <tbody>
                  {depreciation.map((row) => (
                    <tr key={row.yearNumber} className="border-b">
                      <td className="p-2">{row.yearNumber}</td>
                      <td className="p-2">{formatDate(row.yearDate)}</td>
                      <td className="p-2 text-right">{formatCurrency(row.depreciationAmount)}</td>
                      <td className="p-2 text-right">{formatCurrency(row.accumulatedDepreciation)}</td>
                      <td className="p-2 text-right font-medium">{formatCurrency(row.bookValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent key={editingAsset?.id || 'new'} className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAsset ? 'Edit Asset' : 'Add Asset'}</DialogTitle>
            <DialogDescription>
              {editingAsset ? 'Update the asset details below.' : 'Enter the asset details. Depreciation will be calculated automatically.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input name="name" id="name" required defaultValue={editingAsset?.name || ''} />
              </div>
              <div>
                <Label htmlFor="businessProfileId">Business Profile</Label>
                <select
                  name="businessProfileId"
                  id="businessProfileId"
                  defaultValue={editingAsset?.businessProfileId || ''}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">None (General)</option>
                  {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Input name="description" id="description" defaultValue={editingAsset?.description || ''} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="assetType">Asset Type *</Label>
                <select
                  name="assetType"
                  id="assetType"
                  required
                  value={formAssetType}
                  onChange={(e) => setFormAssetType(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select type</option>
                  {ASSET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="depreciationMethod">Depreciation Method *</Label>
                <select
                  name="depreciationMethod"
                  id="depreciationMethod"
                  required
                  defaultValue={editingAsset?.depreciationMethod || 'straight_line'}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {DEPRECIATION_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="purchasePrice">Purchase Price *</Label>
                <Input
                  type="number"
                  name="purchasePrice"
                  id="purchasePrice"
                  step="0.01"
                  min="0.01"
                  required
                  defaultValue={editingAsset?.purchasePrice || ''}
                />
              </div>
              <div>
                <Label htmlFor="salvageValue">Salvage Value</Label>
                <Input
                  type="number"
                  name="salvageValue"
                  id="salvageValue"
                  step="0.01"
                  min="0"
                  defaultValue={editingAsset?.salvageValue ?? 0}
                />
              </div>
              <div>
                <Label htmlFor="usefulLifeYears">Useful Life (yrs) *</Label>
                <Input
                  type="number"
                  name="usefulLifeYears"
                  id="usefulLifeYears"
                  min="1"
                  max="50"
                  required
                  defaultValue={editingAsset?.usefulLifeYears || IRS_USEFUL_LIFE.equipment}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="purchaseDate">Purchase Date *</Label>
                <Input
                  type="date"
                  name="purchaseDate"
                  id="purchaseDate"
                  required
                  defaultValue={editingAsset?.purchaseDate ? editingAsset.purchaseDate.split('T')[0] : ''}
                />
              </div>
              <div>
                <Label htmlFor="datePlacedInService">Date Placed in Service</Label>
                <Input
                  type="date"
                  name="datePlacedInService"
                  id="datePlacedInService"
                  defaultValue={editingAsset?.datePlacedInService ? editingAsset.datePlacedInService.split('T')[0] : ''}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="macrsPropertyClass">MACRS Property Class</Label>
              <select
                name="macrsPropertyClass"
                id="macrsPropertyClass"
                defaultValue={editingAsset?.macrsPropertyClass || ''}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Not applicable</option>
                <option value="3">3-Year Property</option>
                <option value="5">5-Year Property (Vehicles, Computers)</option>
                <option value="7">7-Year Property (Furniture, Equipment)</option>
                <option value="10">10-Year Property</option>
                <option value="15">15-Year Property (Land Improvements)</option>
                <option value="20">20-Year Property</option>
                <option value="27.5">27.5-Year Property (Residential Rental)</option>
                <option value="39">39-Year Property (Nonresidential Real Property)</option>
              </select>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="isSection179"
                  id="isSection179"
                  defaultChecked={editingAsset?.isSection179 || false}
                  className="h-4 w-4"
                />
                <Label htmlFor="isSection179">Section 179 Deduction</Label>
              </div>
              <div className="flex-1">
                <Input
                  type="number"
                  name="section179Amount"
                  id="section179Amount"
                  step="0.01"
                  min="0"
                  placeholder="Section 179 amount"
                  defaultValue={editingAsset?.section179Amount || ''}
                />
              </div>
            </div>

            {formAssetType === 'vehicle' && (
              <div>
                <Label htmlFor="vehicleBusinessUsePct">Business-Use % *</Label>
                <Input
                  type="number"
                  name="vehicleBusinessUsePct"
                  id="vehicleBusinessUsePct"
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="e.g. 80"
                  defaultValue={editingAsset?.vehicleBusinessUsePct ?? ''}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Required for Form 4562 listed-property depreciation. The portion
                  of total use that's business-related (0–100). Personal-use
                  vehicles with mixed business use are listed property under IRS rules.
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea name="notes" id="notes" rows={2} defaultValue={editingAsset?.notes || ''} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingAsset ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
